// ============================================================
// NewInvitationPage.js
// Route /invitations/new — route-based invitation form. Replaces
// the embedded modal flow on InvitationsPage. Fields:
//
//   email           (required, valid email format)
//   displayName     (required, 1-200 chars — backend requirement)
//   clientId        (required, populated from listAdminClients,
//                    pre-fills from ?clientId= query param)
//   roleKey         (required, dropdown)
//   agentEntitlements   (admin role only; multi-checkbox over
//                        modules of the chosen Client; sent as
//                        an array of integer ModuleIDs)
//   accessDurationDays  (auditor role only; integer 1..90)
//
// Submit POSTs to /api/mgmt/invitations/issue. On success the
// page navigates to /users and flashes a success toast via
// location.state. On error the form preserves its state and
// surfaces the failure inline.
//
// Cancel returns to /clients/{clientId} when arriving with a
// pre-filled clientId, otherwise to /users.
// ============================================================

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import Field from '../components/Field';
import { issueInvitation } from '../api/invitations';
import { listAdminClients, listAdminClientModules } from '../api/adminClients';
import { toast } from '../components/Toaster';
import { translateError } from '../utils/errorMessages';
import StatusMessage from '../components/StatusMessage';
import './NewInvitationPage.css';

const DEFAULT_AUDITOR_DAYS = 14;
const MAX_AUDITOR_DAYS = 90;
const MIN_AUDITOR_DAYS = 1;

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

const ROLE_OPTIONS = [
  { value: '',        label: '-- Select role --' },
  { value: 'manager', label: 'Manager' },
  { value: 'carer',   label: 'Carer' },
  { value: 'admin',   label: 'Admin (back-office)' },
  { value: 'auditor', label: 'Auditor' },
];

export default function NewInvitationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const preFilledClientId = searchParams.get('clientId') || '';

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [clientId, setClientId] = useState(preFilledClientId);
  const [roleKey, setRoleKey] = useState('');
  const [selectedModuleIds, setSelectedModuleIds] = useState(new Set());
  const [accessDurationDays, setAccessDurationDays] = useState(DEFAULT_AUDITOR_DAYS);

  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [clients, setClients] = useState([]);
  const [modules, setModules] = useState([]);
  const [modulesLoading, setModulesLoading] = useState(false);

  // Load clients on mount.
  useEffect(() => {
    let cancelled = false;
    listAdminClients({ pageSize: 200 })
      .then((res) => { if (!cancelled) setClients(res.items || []); })
      .catch(() => { /* dropdown stays empty; submit will fail with clearer error */ });
    return () => { cancelled = true; };
  }, []);

  // Load modules whenever a client is chosen (admin role needs them).
  // No-op fetch when clientId is empty.
  useEffect(() => {
    if (!clientId) {
      setModules([]);
      return undefined;
    }
    let cancelled = false;
    setModulesLoading(true);
    listAdminClientModules(parseInt(clientId, 10))
      .then((res) => {
        if (!cancelled) {
          const list = (res.items || []).filter((m) => m.isActiveGlobally && m.isActiveForClient);
          setModules(list);
          // Default selection: all available modules checked.
          setSelectedModuleIds(new Set(list.map((m) => m.moduleId)));
        }
      })
      .catch(() => { if (!cancelled) setModules([]); })
      .finally(() => { if (!cancelled) setModulesLoading(false); });
    return () => { cancelled = true; };
  }, [clientId]);

  function toggleModule(moduleId) {
    setSelectedModuleIds((prev) => {
      const next = new Set(prev);
      if (next.has(moduleId)) next.delete(moduleId); else next.add(moduleId);
      return next;
    });
  }

  const selectedClient = useMemo(
    () => clients.find((c) => String(c.clientId) === String(clientId)) || null,
    [clients, clientId]
  );

  function validate() {
    const errs = {};
    if (!email)                errs.email = 'Email is required.';
    else if (!EMAIL_RE.test(email)) errs.email = 'Enter a valid email address.';
    if (!displayName || displayName.trim().length === 0)
      errs.displayName = 'Display name is required.';
    else if (displayName.length > 200)
      errs.displayName = 'Display name must be 200 characters or fewer.';
    if (!clientId)             errs.clientId = 'Select a Client.';
    if (!roleKey)              errs.roleKey = 'Select a role.';
    if (roleKey === 'admin') {
      if (selectedModuleIds.size === 0)
        errs.agentEntitlements = 'Select at least one module.';
    }
    if (roleKey === 'auditor') {
      const n = Number(accessDurationDays);
      if (!Number.isInteger(n) || n < MIN_AUDITOR_DAYS || n > MAX_AUDITOR_DAYS) {
        errs.accessDurationDays = `Enter a whole number between ${MIN_AUDITOR_DAYS} and ${MAX_AUDITOR_DAYS}.`;
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitError(null);
    if (!validate()) return;

    const body = {
      clientId:    parseInt(clientId, 10),
      email:       email.trim(),
      displayName: displayName.trim(),
      roleKey,
    };
    if (roleKey === 'admin') {
      body.agentEntitlements = Array.from(selectedModuleIds);
    }
    if (roleKey === 'auditor') {
      body.accessDurationDays = parseInt(accessDurationDays, 10);
    }

    setSubmitting(true);
    try {
      const result = await issueInvitation(body);
      const clientName = selectedClient?.clientName || 'the selected Client';
      toast.success(`Invitation issued to ${result.email || body.email} (${clientName})`);
      navigate('/users');
    } catch (err) {
      const translated = translateError(err);
      setSubmitError(translated);
      // Field-specific 400s come back with `field` populated; surface
      // inline.
      if (err && err.field) {
        setErrors((prev) => ({ ...prev, [err.field]: translated }));
      }
    } finally {
      setSubmitting(false);
    }
  }

  function handleCancel() {
    // Came-from heuristic: pre-filled clientId means we were
    // sent here from a Client detail Users tab — return there.
    if (preFilledClientId) {
      navigate(`/clients/${preFilledClientId}?tab=users`);
    } else {
      // If react-router preserved a "from" state, prefer it.
      const from = location.state && location.state.from;
      if (typeof from === 'string') navigate(from);
      else navigate('/users');
    }
  }

  const showAgentField = roleKey === 'admin';
  const showAuditorField = roleKey === 'auditor';

  return (
    <>
      <PageHeader
        title="Invite User"
        subtitle="Generate an invitation link for a new user."
      />

      <form onSubmit={handleSubmit} className="new-invitation" noValidate>
        <Field label="Email" required error={errors.email}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="off"
            className="new-invitation__input"
            disabled={submitting}
          />
        </Field>

        <Field label="Display name" required error={errors.displayName}>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={200}
            autoComplete="off"
            className="new-invitation__input"
            disabled={submitting}
          />
        </Field>

        <Field label="Client" required error={errors.clientId}>
          <select
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            className="new-invitation__input"
            disabled={submitting}
          >
            <option value="">-- Select a Client --</option>
            {clients.map((c) => (
              <option key={c.clientId} value={String(c.clientId)}>{c.clientName}</option>
            ))}
          </select>
        </Field>

        <Field label="Role" required error={errors.roleKey}>
          <select
            value={roleKey}
            onChange={(e) => setRoleKey(e.target.value)}
            className="new-invitation__input"
            disabled={submitting}
          >
            {ROLE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </Field>

        {showAgentField && (
          <Field
            label="Module access"
            required
            help={!clientId
              ? 'Select a Client to see its available modules.'
              : 'Each ticked module is what this admin can open: its records and pages, and its AI agent where it has one. Unticked modules stay closed to them.'}
            error={errors.agentEntitlements}
          >
            <div className="new-invitation__checkboxes" role="group" aria-label="Module access">
              {modulesLoading && (
                <StatusMessage as="p" variant="loading" className="new-invitation__hint">Loading modules…</StatusMessage>
              )}
              {!modulesLoading && modules.length === 0 && clientId && (
                <StatusMessage as="p" variant="empty" className="new-invitation__hint">No modules available for this Client.</StatusMessage>
              )}
              {modules.map((m) => (
                <label key={m.moduleId} className="new-invitation__checkbox">
                  <input
                    type="checkbox"
                    checked={selectedModuleIds.has(m.moduleId)}
                    onChange={() => toggleModule(m.moduleId)}
                    disabled={submitting}
                  />
                  <span>{m.moduleName}</span>
                </label>
              ))}
            </div>
          </Field>
        )}

        {showAuditorField && (
          <Field
            label="Access duration (days)"
            required
            help={`Auditor access expires after this many days from invitation redemption. Default ${DEFAULT_AUDITOR_DAYS} days. Max ${MAX_AUDITOR_DAYS}.`}
            error={errors.accessDurationDays}
          >
            <input
              type="number"
              min={MIN_AUDITOR_DAYS}
              max={MAX_AUDITOR_DAYS}
              value={accessDurationDays}
              onChange={(e) => setAccessDurationDays(e.target.value)}
              className="new-invitation__input new-invitation__input--narrow"
              disabled={submitting}
            />
          </Field>
        )}

        {submitError && (
          <p className="new-invitation__form-error" role="alert">{submitError}</p>
        )}

        <div className="new-invitation__actions">
          <button
            type="button"
            className="new-invitation__cancel"
            onClick={handleCancel}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="new-invitation__submit"
            disabled={submitting}
          >
            {submitting ? 'Sending…' : 'Send Invitation'}
          </button>
        </div>
      </form>
    </>
  );
}
